import { Alert, Button, Group, Modal, NumberInput, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EntitySelect } from '../../components/EntityField';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { formatQty, typedNumber } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { LEDGER_PATH, STOCK_PATH, useStockItems, useStockOnHand } from './stock';
import { Known, type StockActionProps } from './subject';

/**
 * Carrying a case of beer from the dry store out to the bar.
 *
 * Stock is held per area and never per site, which is the whole reason this screen has an area
 * column — but there was no way to say that something had been carried from one to another. Until
 * now the only way to record it was to count BOTH areas afterwards, so in practice it went
 * unrecorded, the bar drifted negative and the dry store drifted high, and the first anyone knew
 * was a stock count that made no sense.
 */
export function MoveStockButton({
  inventoryItemId,
  storageLocationId,
  label,
  compact,
}: StockActionProps = {}) {
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState(false);
  const items = useEntityIndex('inventoryItem');
  const areas = useEntityIndex('storageLocation');
  const stock = useStockItems();
  // Opened from one item, only that item's shelf figures are needed to warn about taking too much.
  const onHand = useStockOnHand(undefined, inventoryItemId);

  const form = useForm({
    initialValues: {
      inventoryItemId: inventoryItemId ?? '',
      // Where a move is started from the row for one shelf, that shelf is where it is coming OUT
      // of — nobody opens the figure for the walk-in to record something arriving there.
      fromStorageLocationId: storageLocationId ?? '',
      toStorageLocationId: '',
      qty: '' as number | '',
    },
    validate: {
      inventoryItemId: (v) => (v ? null : 'Choose what was moved'),
      fromStorageLocationId: (v) => (v ? null : 'Choose where it came from'),
      toStorageLocationId: (v, all) =>
        !v
          ? 'Choose where it went'
          : v === all.fromStorageLocationId
            ? 'It has to go to a different area from the one it came from'
            : null,
      qty: (v) => (typeof v === 'number' && v > 0 ? null : 'Enter how much was moved'),
    },
  });

  const values = form.getValues();
  const unit = stock.unitOf(values.inventoryItemId);

  const close = () => {
    setOpened(false);
    form.reset();
  };

  const move = useMutation({
    mutationFn: (v: typeof values) =>
      rawApi('POST', '/admin/v1/inventory/transfers', {
        inventoryItemId: v.inventoryItemId,
        fromStorageLocationId: v.fromStorageLocationId,
        toStorageLocationId: v.toStorageLocationId,
        qty: Number(v.qty),
        unit,
      }),
    onSuccess: (_result, v) => {
      void queryClient.invalidateQueries({ queryKey: listKey(STOCK_PATH) });
      void queryClient.invalidateQueries({ queryKey: listKey(LEDGER_PATH) });
      notifyDone(
        'Stock moved',
        `${formatQty(typedNumber(v.qty) ?? 0, unit)} of ${items.label(v.inventoryItemId)} now counts against ${areas.label(v.toStorageLocationId)}.`,
      );
      close();
    },
    onError: (err) => notifyError(err, 'record this move'),
  });

  const missingUnit = Boolean(values.inventoryItemId) && !unit;

  // What the books say is on the shelf it is coming off. Shown rather than enforced: the shelf
  // figure is what the move is being recorded to correct, so refusing to move more than it claims
  // would block the very case the manager is standing in front of.
  const heldAtSource = onHand.rows.find(
    (r) =>
      r.inventoryItemId === values.inventoryItemId && r.storageLocationId === values.fromStorageLocationId,
  )?.qtyOnHand;
  const takingTooMuch =
    typeof heldAtSource === 'number' && typeof values.qty === 'number' && values.qty > heldAtSource;

  return (
    <>
      <Button
        size={compact ? 'compact-xs' : 'sm'}
        variant={compact ? 'subtle' : 'light'}
        onClick={() => setOpened(true)}
      >
        {label ?? 'Move stock'}
      </Button>
      <Modal opened={opened} onClose={close} title="Move stock" size="lg" closeOnClickOutside={false}>
        <form onSubmit={form.onSubmit((v) => move.mutate(v))}>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Use this when something is carried from one area to another. Nothing is bought, sold or thrown
              away — one shelf figure goes down and the other goes up by the same amount.
            </Text>

            {inventoryItemId ? (
              <Known label="What was moved" value={items.label(inventoryItemId)} />
            ) : (
              <EntitySelect
                entity="inventoryItem"
                label="What was moved"
                required
                value={values.inventoryItemId || null}
                onChange={(v) => form.setFieldValue('inventoryItemId', v ?? '')}
                error={form.errors.inventoryItemId}
              />
            )}

            <Group grow align="flex-start">
              {storageLocationId ? (
                <Known label="Out of" value={areas.label(storageLocationId)} />
              ) : (
                <EntitySelect
                  entity="storageLocation"
                  label="Out of"
                  required
                  value={values.fromStorageLocationId || null}
                  onChange={(v) => form.setFieldValue('fromStorageLocationId', v ?? '')}
                  error={form.errors.fromStorageLocationId}
                />
              )}
              <EntitySelect
                entity="storageLocation"
                label="Into"
                required
                value={values.toStorageLocationId || null}
                onChange={(v) => form.setFieldValue('toStorageLocationId', v ?? '')}
                error={form.errors.toStorageLocationId}
              />
            </Group>

            <NumberInput
              label="How much"
              required
              min={0}
              suffix={unit ? ` ${unit}` : undefined}
              description={
                missingUnit
                  ? undefined
                  : typeof heldAtSource === 'number'
                    ? `The books say that area holds ${formatQty(heldAtSource, unit)}`
                    : unit
                      ? `Counted in ${unit}, the same as everywhere else for this item`
                      : 'Pick the item first and this will show what it is counted in'
              }
              error={
                missingUnit
                  ? 'This item has no unit set — fill in "Counted in" under Stock items'
                  : form.errors.qty
              }
              {...form.getInputProps('qty')}
            />

            {takingTooMuch && (
              <Alert color="yellow" variant="light" title="That is more than that area is meant to have">
                Record it anyway if that is what you carried. The area it came from will show less than
                nothing, which is a sign a delivery was never entered — count it when you get the chance.
              </Alert>
            )}
          </Stack>
          <Group justify="flex-end" mt="xl">
            <Button variant="default" onClick={close} disabled={move.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={move.isPending} disabled={missingUnit}>
              Record the move
            </Button>
          </Group>
        </form>
      </Modal>
    </>
  );
}
